import {
  Bookmark,
  Brain,
  ChevronRight,
  Copy,
  MoreHorizontal,
  RotateCcw,
  ThumbsDown,
  ThumbsUp,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import type { AiProvider } from "@/desktop/ui";
import { cn, copyToClipboard } from "@/lib/utils";
import { useChatHistory } from "./chat-sessions";
import { Menu, MenuContent, MenuItem, MenuTrigger, Tip } from "./ui/overlay";

/**
 * Peças comuns dos chats de IA (Copilot e Claude), para os dois funcionarem do mesmo jeito: o
 * raciocínio ao vivo, e no fim de cada resposta avaliar, copiar, favoritar, o modelo e o tempo.
 */

/** Raciocínio do modelo: aberto e rolando enquanto ele pensa; depois, recolhido com o tempo. */
export function Thought({ text, live, ms }: { text: string; live: boolean; ms?: number }) {
  const [open, setOpen] = useState<boolean | null>(null);
  const shown = open ?? live;
  const ref = useRef<HTMLParagraphElement>(null);
  useEffect(() => {
    if (live && ref.current) ref.current.scrollTop = ref.current.scrollHeight;
  }, [text, live]);
  return (
    <div className="text-[12px] text-fg-subtle">
      <button
        type="button"
        onClick={() => setOpen(!shown)}
        className="flex items-center gap-1.5 hover:text-fg-muted"
        aria-expanded={shown}
      >
        <Brain className={cn("h-3.5 w-3.5", live && "animate-pulse")} />
        {live ? "Pensando…" : ms ? `Pensou por ${Math.max(1, Math.round(ms / 1000))} s` : "Raciocínio"}
        <ChevronRight className={cn("h-3 w-3 transition-transform", shown && "rotate-90")} />
      </button>
      {shown ? (
        <p
          ref={ref}
          className="mt-1 max-h-40 overflow-y-auto whitespace-pre-wrap border-l border-border pl-2 leading-relaxed"
        >
          {text}
        </p>
      ) : null}
    </div>
  );
}

const DOWN_REASONS = ["Não entendeu o pedido", "Código errado", "Incompleta", "Lenta demais"];
const FEEDBACK_KEY = "kompil.ai.feedback";

/** Avaliações das respostas (ficam neste computador; ajudam a escolher modelo e modo). */
export function saveFeedback(entry: {
  provider: AiProvider;
  rating: "up" | "down";
  model: string | null;
  reason?: string;
}) {
  try {
    const list = JSON.parse(localStorage.getItem(FEEDBACK_KEY) ?? "[]") as unknown[];
    localStorage.setItem(FEEDBACK_KEY, JSON.stringify([...list, { ...entry, at: Date.now() }].slice(-300)));
  } catch {
    // sem armazenamento: a avaliação vale só na tela
  }
}

/**
 * Fim da resposta, como no VS Code: ↻ Repetir, 👍, 👎 (com o motivo) e o menu ⋯ (copiar,
 * favoritar). O modelo que respondeu e o tempo ficam discretos à direita.
 */
export function ResponseActions({
  provider,
  sessionId,
  index,
  text,
  model,
  seconds,
  rating,
  onRate,
  onRetry,
}: {
  provider: AiProvider;
  sessionId: string | null;
  index: number;
  text: string;
  model?: string | null;
  seconds?: number | null;
  rating?: "up" | "down";
  onRate(rating: "up" | "down"): void;
  /** Pedir de novo (a mesma pergunta). */
  onRetry?: () => void;
}) {
  const [asking, setAsking] = useState(false);
  const bookmarked = useChatHistory((s) =>
    Boolean(sessionId && s.sessions[provider].find((x) => x.id === sessionId)?.bookmarks.includes(index)),
  );
  const rate = (r: "up" | "down", reason?: string) => {
    onRate(r);
    saveFeedback({ provider, rating: r, model: model ?? null, reason });
    setAsking(false);
    toast(r === "up" ? "Obrigado pela avaliação!" : "Avaliação registrada", { duration: 1500 });
  };
  const btn =
    "flex h-6 w-6 items-center justify-center rounded-md transition-colors hover:bg-bg-muted hover:text-fg";
  return (
    <div className="pt-1.5">
      <div className="flex items-center gap-0.5 text-fg-subtle">
        {onRetry ? (
          <Tip label="Repetir">
            <button type="button" className={btn} onClick={onRetry} aria-label="Repetir">
              <RotateCcw className="h-3.5 w-3.5" />
            </button>
          </Tip>
        ) : null}
        <Tip label="Útil">
          <button
            type="button"
            className={cn(btn, rating === "up" && "text-fg")}
            onClick={() => rate("up")}
            aria-label="Útil"
            aria-pressed={rating === "up"}
          >
            <ThumbsUp className={cn("h-3.5 w-3.5", rating === "up" && "fill-current")} />
          </button>
        </Tip>
        <Tip label="Não ajudou">
          <button
            type="button"
            className={cn(btn, rating === "down" && "text-fg")}
            onClick={() => setAsking(true)}
            aria-label="Não ajudou"
            aria-pressed={rating === "down"}
          >
            <ThumbsDown className={cn("h-3.5 w-3.5", rating === "down" && "fill-current")} />
          </button>
        </Tip>
        <Menu>
          <Tip label="Mais ações">
            <MenuTrigger asChild>
              <button type="button" className={btn} aria-label="Mais ações da resposta">
                <MoreHorizontal className="h-3.5 w-3.5" />
              </button>
            </MenuTrigger>
          </Tip>
          <MenuContent align="start" className="w-60">
            {text ? (
              <MenuItem
                icon={<Copy className="h-3.5 w-3.5" />}
                onSelect={() =>
                  void copyToClipboard(text).then(() => toast("Resposta copiada", { duration: 1200 }))
                }
              >
                Copiar a resposta
              </MenuItem>
            ) : null}
            {sessionId ? (
              <MenuItem
                icon={<Bookmark className={cn("h-3.5 w-3.5", bookmarked && "fill-current")} />}
                onSelect={() => useChatHistory.getState().toggleMessage(provider, sessionId, index)}
              >
                {bookmarked ? "Tirar dos favoritos" : "Favoritar a resposta"}
              </MenuItem>
            ) : null}
          </MenuContent>
        </Menu>
        {bookmarked ? (
          <Bookmark className="ml-0.5 h-3 w-3 fill-current text-fg-muted" aria-label="Favorita" />
        ) : null}
        <span className="ml-auto truncate pl-2 text-[11px]" title={model ?? undefined}>
          {model ?? ""}
          {model && seconds ? " · " : ""}
          {seconds ? `${seconds} s` : ""}
        </span>
      </div>
      {asking ? (
        <div className="mt-1.5 flex flex-wrap gap-1">
          {DOWN_REASONS.map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => rate("down", r)}
              className="rounded-full border border-border px-2 py-0.5 text-[11.5px] text-fg-muted hover:border-border-strong hover:text-fg"
            >
              {r}
            </button>
          ))}
          <button
            type="button"
            onClick={() => rate("down")}
            className="rounded-full px-2 py-0.5 text-[11.5px] text-fg-subtle hover:text-fg"
          >
            Pular
          </button>
        </div>
      ) : null}
    </div>
  );
}
